import {
  useQueryClient,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query';

import { FetchError } from './fetchError';
import type { MutationFunctionContext } from './queryFunction';

// throw to error boundaries if we run into server errors
// other types of errors should be handled by the UI
export const useErrorBoundary = (error: unknown) => {
  if (error instanceof FetchError) {
    if (error.statusCode >= 500) {
      return true;
    }
  }

  return false;
};

/**
 * Provides optimistic mutation callbacks for use with react-query.
 * These callbacks allow for optimistic UI updates by caching the previous data
 * and rolling it back if the mutation fails.
 *
 * @param params - The parameters for the optimistic mutation callbacks.
 * @param params.key - The query key for the data being mutated.
 * @param params.queryOptions - Optional mutation options to be passed to the underlying mutation.
 * @returns - The optimistic mutation callbacks.
 */
export const useOptimisticMutationCallbacks = <
  TData,
  TError = unknown,
  TVars = unknown,
>({
  key,
  queryOptions,
}: {
  key: QueryKey;
  queryOptions?: UseMutationOptions<
    TData,
    TError,
    TVars,
    MutationFunctionContext<TData>
  >;
}): Pick<
  UseMutationOptions<TData, TError, TVars, MutationFunctionContext<TData>>,
  'onMutate' | 'onError' | 'onSettled'
> => {
  const queryClient = useQueryClient();

  return {
    onMutate: async (vars: TVars) => {
      // Run given callback
      queryOptions?.onMutate?.(vars);

      // Cancel any outgoing fetches
      await queryClient.cancelQueries({ queryKey: key });

      // Snapshot the previous value
      const prev = queryClient.getQueryData<TData>(key);

      // Return context so it's accessible in onError/onSettled
      return { prev };
    },
    onError: (...args) => {
      // Run given callback
      queryOptions?.onError?.(...args);

      // Roll back data (if we have a context snapshot)
      const [, , context] = args;
      if (context?.prev) {
        queryClient.setQueryData(key, context.prev);
      }
    },
    onSettled: (...args) => {
      // Run given callback
      queryOptions?.onSettled?.(...args);

      // Invalidate to refetch fresh data
      queryClient.invalidateQueries({ queryKey: key });
    },
  };
};
